import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useContent, useLang } from "../i18n/LanguageContext";
import legal, { LEGAL_SLUGS } from "../i18n/legal";
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowLeft, ChevronRight } from "lucide-react";

function LegalTopBar() {
  const c = useContent();
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#eee]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-[#16233d] flex items-center justify-center">
            <span className="font-serif-lux text-2xl text-[#b08d57] leading-none">B</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-lux text-xl text-[#16233d] font-semibold">{c.nav.brand}</div>
            <div className="text-[10px] tracking-[0.2em] text-[#8a8f9c]">{c.nav.brandSub}</div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/" className="hidden sm:flex items-center gap-1.5 text-sm text-[#3a4258] hover:text-[#b08d57] transition-colors">
            <ArrowLeft size={16} /> {c.portal.backHome}
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Legal({ type }) {
  const { lang } = useLang();
  const c = useContent();
  const L = legal[lang] || legal.de;
  const data = L[type];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  if (!data) return null;

  return (
    <div className="min-h-screen bg-white">
      <LegalTopBar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="eyebrow mb-3">{c.footer.legalTitle}</div>
        <h1 className="font-serif-lux text-4xl lg:text-5xl text-[#16233d] font-semibold">{data.title}</h1>
        <p className="text-sm text-[#8a8f9c] mt-3">{L.updatedLabel}</p>

        <div className="mt-10 space-y-8">
          {data.blocks.map((b, i) => (
            <section key={i}>
              {b.h && <h2 className="font-serif-lux text-2xl text-[#16233d] mb-3">{b.h}</h2>}
              {b.p && b.p.map((para, j) => (
                <p key={j} className="text-[#5c6577] leading-relaxed mb-2.5">{para}</p>
              ))}
              {b.list && (
                <ul className="space-y-2.5 mt-2">
                  {b.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-[#5c6577]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#b08d57] mt-2 shrink-0" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-14 border-t border-[#eee] pt-8">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8a8f9c] mb-4">{c.footer.legalTitle}</div>
          <div className="flex flex-wrap gap-3">
            {L.order.filter((t) => t !== type).map((t) => (
              <Link
                key={t}
                to={LEGAL_SLUGS[t]}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#e7e2d8] px-4 py-2 text-sm text-[#3a4258] hover:border-[#b08d57] hover:text-[#8f6f3f] transition-colors"
              >
                {L.labels[t]} <ChevronRight size={14} />
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
